import { useEffect, useMemo, useRef, useState } from "react";
import {
  capcutDraftToSrt,
  type ConversionMode,
  type ConversionResult,
} from "@capcut-srt/core";
import confetti from "canvas-confetti";
import { Header } from "./Header";
import { SEOHead } from "./SEOHead";
import { Dropzone } from "./Dropzone";
import { StatusPanel } from "./StatusPanel";
import { DiagnosticsPanel } from "./DiagnosticsPanel";
import { WarningsPanel } from "./WarningsPanel";
import { Footer } from "./Footer";
import { Tooltip } from "./Tooltip";
import { useLocale } from "../hooks/useLocale";

type AppState =
  | { status: "idle" }
  | { status: "processing"; fileName: string }
  | { status: "success"; fileName: string; result: ConversionResult }
  | { status: "empty"; fileName: string; result: ConversionResult }
  | { status: "error"; fileName?: string; message: string };

/**
 * Main application component composing all UI sections.
 * Contains all state management, file handling, and conversion logic.
 */
export function App() {
  const { t } = useLocale();
  const [mode, setMode] = useState<ConversionMode>("segment");
  const [fixCapitalization, setFixCapitalization] = useState(false);
  const [state, setState] = useState<AppState>({ status: "idle" });
  const [isDragging, setIsDragging] = useState(false);
  const [hasWordData, setHasWordData] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const parsedJsonRef = useRef<unknown>(null);
  const fileNameRef = useRef<string>("");

  const result =
    state.status === "success" || state.status === "empty"
      ? state.result
      : undefined;
  const preview = useMemo(() => result?.cues.slice(0, 8) ?? [], [result]);

  // Re-run conversion reactively when mode or fixCapitalization changes
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Skip on mount — only react to subsequent changes
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const json = parsedJsonRef.current;
    const fileName = fileNameRef.current;
    if (!json || !fileName) return;

    try {
      const conversion = capcutDraftToSrt(json, {
        mode: hasWordData ? mode : "segment",
        fixCapitalization,
      });
      if (conversion.subtitleCount === 0) {
        setState({ status: "empty", fileName, result: conversion });
      } else {
        setState({ status: "success", fileName, result: conversion });
      }
    } catch {
      // Reconversion shouldn't fail if initial parse succeeded
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, fixCapitalization]);

  async function handleFile(file: File) {
    setState({ status: "processing", fileName: file.name });

    if (!file.name.endsWith(".json") && file.type !== "application/json") {
      setState({
        status: "error",
        message: "Solo se permiten archivos JSON.",
        fileName: file.name,
      });
      return;
    }

    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      setState({
        status: "error",
        message: "El archivo es demasiado grande (máximo 10MB).",
        fileName: file.name,
      });
      return;
    }

    try {
      const text = await file.text();
      const json = JSON.parse(text);

      // Store parsed JSON for reactive reconversion
      parsedJsonRef.current = json;
      fileNameRef.current = file.name;

      const hasWords = json.materials?.texts?.some(
        (t: any) =>
          t.words &&
          ((Array.isArray(t.words) && t.words.length > 0) ||
            (t.words.text && t.words.text.length > 0))
      );

      setHasWordData(Boolean(hasWords));

      // Minimum loading display: if processing completes in under 300ms,
      // show loading for at least 500ms total to prevent a flash of content.
      const processingStart = Date.now();

      const conversion = capcutDraftToSrt(json, {
        mode: hasWords ? mode : "segment",
        fixCapitalization,
      });

      const elapsed = Date.now() - processingStart;
      if (elapsed < 300) {
        const remaining = 500 - elapsed;
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }

      console.info("[CapCut SRT] Conversion diagnostics", {
        fileName: file.name,
        fileSize: file.size,
        mode,
        subtitleCount: conversion.subtitleCount,
        warnings: conversion.warnings,
        sources: conversion.sources,
        diagnostics: conversion.diagnostics,
      });

      if (!hasWords && mode === "word") {
        setMode("segment");
      }

      if (conversion.subtitleCount === 0) {
        setState({ status: "empty", fileName: file.name, result: conversion });
        return;
      }

      setState({ status: "success", fileName: file.name, result: conversion });

      confetti({
        particleCount: 150,
        spread: 80,
        origin: { y: 0.6 },
        colors: ["#1d6b56", "#f2ff75", "#15231f"],
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "No se pudo convertir el archivo.";
      setState({ status: "error", fileName: file.name, message });
    }
  }

  function downloadFile(content: string, filename: string, type: string) {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  function downloadSrt() {
    if (!result) return;
    downloadFile(
      result.srt,
      "subtitles.srt",
      "application/x-subrip;charset=utf-8"
    );
  }

  function downloadTxt() {
    if (!result) return;
    const text = result.cues.map((cue) => cue.text).join("\n");
    downloadFile(text, "subtitles.txt", "text/plain;charset=utf-8");
  }

  function downloadTxtWithTime() {
    if (!result) return;
    const text = result.cues
      .map(
        (cue) =>
          `[${formatPreviewRange(cue.startUs, cue.endUs)}] ${cue.text}`
      )
      .join("\n");
    downloadFile(text, "subtitles_time.txt", "text/plain;charset=utf-8");
  }

  return (
    <div className="app-layout">
      <SEOHead />
      <main className="app-shell">
        <section className="workspace" aria-label="CapCut SRT workspace">
          <Header
            mode={mode}
            onModeChange={setMode}
            hasWordData={hasWordData}
            isIdle={state.status === "idle"}
          />

          {(result?.hasCapitalStyling ?? false) && (
            <div className="capitalization-bar">
              <div className="capitalization-controls">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={fixCapitalization}
                    onChange={(e) => setFixCapitalization(e.target.checked)}
                  />
                  <span className="toggle-track" aria-hidden="true">
                    <span className="toggle-thumb" />
                  </span>
                  <span className="toggle-label">{t("header.fixCapitalization")}</span>
                </label>
                <Tooltip content={t("header.fixCapitalizationTooltip")}>
                  <span className="info-icon" aria-label={t("header.fixCapitalizationTooltip")}>ⓘ</span>
                </Tooltip>
              </div>
              <span className="options-banner">{t("header.capitalDetected")}</span>
            </div>
          )}

          <div className="content-grid">
            <Dropzone
              status={state.status}
              isDragging={isDragging}
              fileName={
                state.status !== "idle" ? (state as any).fileName : undefined
              }
              errorMessage={
                state.status === "error" ? state.message : undefined
              }
              fileInputRef={fileInputRef}
              onFile={(file) => void handleFile(file)}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragging(false);
                const file = event.dataTransfer.files.item(0);
                if (file) void handleFile(file);
              }}
            />

            <StatusPanel
              status={state.status}
              result={result}
              preview={preview}
              errorMessage={
                state.status === "error" ? state.message : undefined
              }
              onDownloadSrt={downloadSrt}
              onDownloadTxt={downloadTxt}
              onDownloadTxtTime={downloadTxtWithTime}
            />
          </div>

          {result ? (
            <DiagnosticsPanel diagnostics={result.diagnostics} />
          ) : null}

          <WarningsPanel warnings={result?.warnings ?? []} />
        </section>
      </main>
      <Footer />
    </div>
  );
}

function formatPreviewRange(startUs: number, endUs: number): string {
  const start = secondsLabel(startUs);
  const end = secondsLabel(endUs);
  return `${start} - ${end}`;
}

function secondsLabel(microseconds: number): string {
  return `${(microseconds / 1_000_000).toFixed(3)}s`;
}
